import { Outlet, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkles, BookOpen, Home, FlaskConical } from "lucide-react";
import { useState } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_arquetipos-app/artifacts/93ovrho7_Alquimia.png";

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { path: "/", label: "Inicio", icon: Home },
    { path: "/introduccion", label: "¿Qué son?", icon: Sparkles },
    { path: "/test", label: "Hacer Test", icon: FlaskConical },
    { path: "/biblioteca", label: "Biblioteca", icon: BookOpen },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#0F0518] relative overflow-x-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 bg-cosmic pointer-events-none" />
      <div 
        className="fixed inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1630873273144-cfa9079fdd72?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      />
      <div className="fixed inset-0 bg-[#0F0518]/70 pointer-events-none" />

      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
              <img 
                src={LOGO_URL} 
                alt="Alquimia del Alma" 
                className="h-10 md:h-12 w-auto transition-transform group-hover:scale-105"
              />
              <span className="hidden sm:block font-heading text-lg md:text-xl text-primary-100 group-hover:text-white transition-colors">
                Alquimia del Alma
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, '')}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full font-body text-sm transition-all ${
                      isActive(item.path)
                        ? "bg-primary/30 text-white border border-primary/50"
                        : "text-primary-200 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Icon size={16} />
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-primary-200 hover:text-white transition-colors"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden glass-card border-t border-white/5"
            >
              <div className="px-4 py-4 space-y-2">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg font-body transition-all ${
                        isActive(item.path)
                          ? "bg-primary/30 text-white"
                          : "text-primary-200 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      <Icon size={20} />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-16 md:pt-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="relative z-10 glass-card border-t border-white/5 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Brand */}
            <div className="space-y-4">
              <img src={LOGO_URL} alt="Alquimia del Alma" className="h-12 w-auto" />
              <p className="text-primary-200 text-sm font-body leading-relaxed">
                Explorando las profundidades del alma a través de la psicología analítica,
                Internal Family Systems y neurociencia.
              </p>
              {/* Insignia oficial de Google Play. La imagen es la que distribuye Google:
                  sus normas de marca solo permiten escalarla, nunca recortarla ni recolorearla. */}
              <a
                href="https://play.google.com/store/apps/details?id=com.alquimiadelalma.app"
                target="_blank"
                rel="noopener noreferrer"
                data-umami-event="app-play-arquetipos"
                aria-label="Descargar la app Alquimia del Alma en Google Play"
                className="inline-block leading-none"
              >
                <img
                  src="/google-play-badge.png"
                  alt="Disponible en Google Play"
                  width="150"
                  height="58"
                  loading="lazy"
                  className="h-[58px] w-auto block"
                />
              </a>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h3 className="font-heading text-lg text-white">Contacto</h3>
              <div className="space-y-2 text-sm text-primary-200 font-body">
                <p>agenda@alquimiadelalma.click</p>
                <p>+57 300 554 2958</p>
              </div>
            </div>

            {/* Links */}
            <div className="space-y-4">
              <h3 className="font-heading text-lg text-white">Explorar</h3>
              <div className="space-y-2 text-sm font-body">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block text-primary-200 hover:text-secondary transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/5 text-center">
            <p className="text-primary-300 text-sm font-body">
              © {new Date().getFullYear()} Alquimia del Alma. Desarrollado con amor para tu viaje interior.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
